import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, combineLatest, debounceTime, filter, map, of, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { listWidgetLocations$ } from '@vendasta/protos/conversation/v1/web_chat_widget';

export interface LocationRow {
  businessName: string;
  address: string;
  namespace: string;
}

interface FetchResult {
  locations: LocationRow[];
  nextCursor: string | null;
  hasMore: boolean;
  loading: boolean;
  error: boolean;
}

const SEARCH_DEBOUNCE_MS = 200;

@Injectable()
export class WebchatLocationService {
  private readonly widgetId$$ = new BehaviorSubject<string>('');
  private readonly searchTerm$$ = new BehaviorSubject<string>('');
  private readonly cursor$$ = new BehaviorSubject<string | null>(null);
  private readonly cursorStack: (string | null)[] = [];
  private lastNextCursor: string | null = null;

  private readonly result$: Observable<FetchResult> = combineLatest([this.searchTerm$$, this.cursor$$, this.widgetId$$]).pipe(
    filter(([, , widgetId]) => widgetId !== ''),
    debounceTime(SEARCH_DEBOUNCE_MS),
    switchMap(([term, cursor, widgetId]) =>
      this.fetch(widgetId, term, cursor).pipe(
        map((r) => ({ ...r, loading: false, error: false })),
        startWith<FetchResult>({ locations: [], nextCursor: null, hasMore: false, loading: true, error: false }),
        catchError(() => of<FetchResult>({ locations: [], nextCursor: null, hasMore: false, loading: false, error: true })),
      ),
    ),
    tap((r) => { if (!r.loading) this.lastNextCursor = r.nextCursor; }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly locations$: Observable<LocationRow[]> = this.result$.pipe(map((r) => r.locations));
  readonly isLoading$: Observable<boolean> = this.result$.pipe(map((r) => r.loading));
  readonly hasMore$: Observable<boolean> = this.result$.pipe(map((r) => r.hasMore));
  readonly hasPrev$: Observable<boolean> = this.cursor$$.pipe(map(() => this.cursorStack.length > 0));
  readonly error$: Observable<boolean> = this.result$.pipe(map((r) => r.error));

  init(widgetId: string): void {
    this.widgetId$$.next(widgetId);
  }

  setSearchTerm(term: string): void {
    this.cursorStack.length = 0;
    this.cursor$$.next(null);
    this.searchTerm$$.next(term);
  }

  nextPage(): void {
    this.cursorStack.push(this.cursor$$.value);
    this.cursor$$.next(this.lastNextCursor);
  }

  prevPage(): void {
    const prev = this.cursorStack.pop() ?? null;
    this.cursor$$.next(prev);
  }

  private fetch(widgetId: string, term: string, cursor: string | null): Observable<Omit<FetchResult, 'loading' | 'error'>> {
    return listWidgetLocations$({ widgetId, query: term, cursor: cursor ?? undefined, pageSize: 10 }).pipe(
      map((r) => ({
        locations: r.locations?.map((loc) => ({
          businessName: loc.businessName,
          namespace: loc.namespace,
          address: [loc.address, loc.address2, loc.city, [loc.state, loc.zip].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', '),
        })) ?? [],
        nextCursor: r.nextCursor || null,
        hasMore: r.hasMore ?? false,
      })),
    );
  }
}
